import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { DatePicker, Form, Input, Radio, Spin, TimePicker, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import * as Icon from 'base/ui/components/Icons';
import PageWrapper from 'pages/Components/PageWrapper';
import CustomButton from 'base/ui/components/Button';
import BoxContainer from 'base/ui/components/BoxContainer';
import { usePermission } from 'hooks/usePermission';
import { SYSTEM_TITLE } from 'utils/constants';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomInput from 'base/ui/components/CustomInput';
import { customFilterOption } from 'utils/searchHelper';
import {
    wasteHandoverPlanPresenter,
    commonPresenter,
    commonMasterDataPresenter,
    commonMasterDataStore,
    commonStore,
} from 'stores/root_store';

const createUpdateWasteHandoverPlan = () => {
    return observer(() => {
        const { t } = useTranslation();
        const { id: wasteHandoverPlanId } = useParams();
        const navigate = useNavigate();
        const [form] = Form.useForm();

        usePermission('EDIT');
        document.title = t('page-title.update') + ' - ' + SYSTEM_TITLE;

        const [loading, setLoading] = useState(true);
        const [saving, setSaving] = useState(false);
        const [detailId, setDetailId] = useState<string>();

        const [wasteOptions, setWasteOptions] = useState<{ label: string; value: string }[]>([]);
        const [vendorOptions, setVendorOptions] = useState<{ label: string; value: string }[]>([]);
        const [storageOptions, setStorageOptions] = useState<{ label: string; value: string }[]>([]);

        const loadWasteOptions = async () => {
            try {
                const result = await commonPresenter.getDropdownProductWaste();
                if (result && result.length > 0) {
                    setWasteOptions(
                        result.map((item) => ({
                            label: item.value || '',
                            value: item.key || '',
                        }))
                    );
                }
            } catch (error) {
                console.error('Error loading waste options:', error);
            }
        };

        const loadVendorOptions = async () => {
            try {
                await commonPresenter.getDropdownVendor(commonStore, { type: 'NCC_CT' });
                if (commonStore.commonRespone.isSuccess) {
                    setVendorOptions(
                        commonStore.commonRespone.data.map((item) => ({
                            label: item.value || '',
                            value: item.key || '',
                        }))
                    );
                }
            } catch (error) {
                console.error('Error loading vendor options:', error);
            }
        };

        const loadStorageOptions = async () => {
            try {
                await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'StockType');
                const stockTypes = commonMasterDataStore.commonRespone?.data || [];
                const wasteType = stockTypes.find((item) => item.value?.toUpperCase() === 'KCT');
                const type = wasteType?.key || 'KCT';

                await commonPresenter.getDropdownStock(commonStore, { type });
                if (commonStore.commonRespone.isSuccess) {
                    setStorageOptions(
                        commonStore.commonRespone.data.map((item) => ({
                            label: item.value || '',
                            value: item.key || '',
                        }))
                    );
                }
            } catch (error) {
                console.error('Error loading storage options:', error);
            }
        };

        const getDetail = async () => {
            if (!wasteHandoverPlanId) return;
            setLoading(true);
            try {
                const response = await wasteHandoverPlanPresenter.getWasteHandoverPlanDetail(wasteHandoverPlanId);
                if (response.isSuccess && response.data) {
                    const detail = response.data;
                    setDetailId(detail.wasteHandoverPlanId);
                    const deliveryDate = detail.deliveryDate ? dayjs(detail.deliveryDate) : undefined;
                    form.setFieldsValue({
                        deliveryDate: deliveryDate ? deliveryDate.startOf('day') : undefined,
                        deliveryTime: deliveryDate ? deliveryDate : undefined,
                        wasteCode: detail.productCode,
                        wasteName: detail.productName,
                        vendorId: detail.vendorNumber,
                        storageLocation: detail.storageLocation,
                        deliveryNumber: detail.handoverDocumentNumber,
                        note: detail.note,
                        actived: detail.actived ?? true,
                    });
                } else {
                    message.error(response.message || 'Không tải được dữ liệu.');
                }
            } catch (error) {
                console.error('Error loading detail:', error);
                message.error('Có lỗi xảy ra khi tải dữ liệu.');
            } finally {
                setLoading(false);
            }
        };

        const handleSubmit = async (values: any, isContinue?: boolean) => {
            if (!detailId && !wasteHandoverPlanId) {
                message.error('Không xác định được bản ghi cần cập nhật');
                return;
            }
            setSaving(true);
            try {
                // Gộp ngày và giờ lại
                let deliveryDateTime = dayjs();
                if (values.deliveryDate) {
                    deliveryDateTime = dayjs(values.deliveryDate);
                    if (values.deliveryTime) {
                        const time = dayjs(values.deliveryTime);
                        deliveryDateTime = deliveryDateTime
                            .hour(time.hour())
                            .minute(time.minute())
                            .second(time.second());
                    }
                } else if (values.deliveryTime) {
                    const time = dayjs(values.deliveryTime);
                    deliveryDateTime = dayjs()
                        .hour(time.hour())
                        .minute(time.minute())
                        .second(time.second());
                }

                const payload = {
                    wasteHandoverPlanId: detailId || wasteHandoverPlanId || '',
                    // Format thành chuỗi local không kèm timezone để tránh bị trừ 7 giờ
                    deliveryDate: deliveryDateTime.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                    wasteGroupCode: values.wasteGroupCode || '',
                    productCode: values.wasteCode || '',
                    productName: values.wasteName || '',
                    vendorNumber: values.vendorId || '',
                    vendorName: vendorOptions.find((v) => v.value === values.vendorId)?.label || '',
                    storageLocation: values.storageLocation || '',
                    handoverDocumentNumber: values.deliveryNumber || '',
                    note: values.note || '',
                    actived: values.actived !== undefined ? values.actived : true,
                };

                const response = await wasteHandoverPlanPresenter.updateWasteHandoverPlan(payload);
                if (response.isSuccess) {
                    message.success(t('message.update-success'));
                    if (isContinue) {
                        getDetail();
                    } else {
                        navigate('/WasteHandoverPlan');
                    }
                } else {
                    message.error(response.message || 'Có lỗi xảy ra khi cập nhật');
                }
            } catch (error) {
                console.error('Error updating waste handover plan:', error);
                message.error('Có lỗi xảy ra khi cập nhật kế hoạch.');
            } finally {
                setSaving(false);
            }
        };

        useEffect(() => {
            Promise.allSettled([loadWasteOptions(), loadVendorOptions(), loadStorageOptions()]).finally(() =>
                setLoading(false)
            );
        }, []);

        useEffect(() => {
            if (wasteHandoverPlanId) {
                getDetail();
            }
        }, [wasteHandoverPlanId]);

        return (
            <PageWrapper
                breadcrumb={[
                    {
                        text: t('waste-handover-plan.title'),
                        to: '/WasteHandoverPlan',
                    },
                    {
                        text: t('page-title.update'),
                    },
                ]}
                extras={
                    <>
                        <CustomButton
                            icon={<Icon.Download className="!fill-[#262626]" />}
                            disabled={saving}
                            loading={saving}
                            onClick={() => {
                                form
                                    .validateFields()
                                    .then((values: any) => {
                                        handleSubmit(values);
                                    })
                                    .catch((errors) => console.log(errors));
                            }}
                        >
                            {t('btn.save')}
                        </CustomButton>
                        <CustomButton
                            type="primary"
                            icon={<Icon.Download />}
                            disabled={saving}
                            loading={saving}
                            onClick={() => {
                                form
                                    .validateFields()
                                    .then((values: any) => {
                                        handleSubmit(values, true);
                                    })
                                    .catch((errors) => console.log(errors));
                            }}
                        >
                            {t('btn.save-update')}
                        </CustomButton>
                    </>
                }
            >
                <BoxContainer fullHeight>
                    <Spin spinning={loading}>
                        <SearchForm
                            layout="vertical"
                            autoComplete="off"
                            className="gap-x-12"
                            column="col-2"
                            form={form}
                        >
                            <SearchItem
                                name="deliveryDate"
                                label={t('waste-handover-plan.delivery-schedule-time')}
                                rules={[
                                    {
                                        required: true,
                                        message: t('waste-handover-plan.delivery-schedule-time-required'),
                                    },
                                ]}
                                className="col-span-2"
                            >
                                <div className="flex gap-2">
                                    <Form.Item name="deliveryDate" noStyle>
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            placeholder={t('place-holder.select')}
                                            style={{ width: '50%' }}
                                        />
                                    </Form.Item>
                                    <Form.Item name="deliveryTime" noStyle>
                                        <TimePicker
                                            format="HH:mm"
                                            placeholder="hh:mm"
                                            style={{ width: '50%' }}
                                        />
                                    </Form.Item>
                                </div>
                            </SearchItem>
                            <SearchItem
                                name="wasteCode"
                                label={t('waste-handover-plan.waste')}
                                rules={[{ required: true, message: t('waste-handover-plan.waste-required') }]}
                            >
                                <CustomSelect
                                    options={wasteOptions}
                                    allowClear
                                    showSearch
                                    placeholder={t('place-holder.select')}
                                    filterOption={customFilterOption}
                                    onChange={(value, option: any) => {
                                        form.setFieldValue('wasteName', option?.label || '');
                                    }}
                                />
                            </SearchItem>
                            <Form.Item name="wasteName" hidden>
                                <input type="hidden" />
                            </Form.Item>
                            <SearchItem
                                name="vendorId"
                                label={t('waste-handover-plan.vendor')}
                                rules={[{ required: true, message: t('waste-handover-plan.vendor-required') }]}
                            >
                                <CustomSelect
                                    options={vendorOptions}
                                    allowClear
                                    showSearch
                                    placeholder={t('place-holder.select')}
                                    filterOption={customFilterOption}
                                />
                            </SearchItem>
                            <SearchItem
                                name="storageLocation"
                                label={t('waste-handover-plan.storage-location')}
                                rules={[
                                    { required: true, message: t('waste-handover-plan.storage-location-required') },
                                ]}
                            >
                                <CustomSelect
                                    options={storageOptions}
                                    allowClear
                                    showSearch
                                    placeholder={t('place-holder.select')}
                                    filterOption={customFilterOption}
                                />
                            </SearchItem>
                            <SearchItem
                                name="deliveryNumber"
                                label={t('waste-handover-plan.delivery-number')}
                            >
                                <CustomInput placeholder={t('waste-handover-plan.delivery-number')} />
                            </SearchItem>
                            <SearchItem name="note" label={t('waste-handover-plan.note')}>
                                <Input.TextArea
                                    rows={1}
                                    autoSize={{ minRows: 1 }}
                                    placeholder={t('waste-handover-plan.note')}
                                />
                            </SearchItem>
                            <SearchItem name="actived" label={t('waste-handover-plan.status')} initialValue={true}>
                                <Radio.Group>
                                    <Radio value={true}>{t('radio-btn.active')}</Radio>
                                    <Radio value={false}>{t('radio-btn.in-active')}</Radio>
                                </Radio.Group>
                            </SearchItem>
                        </SearchForm>
                    </Spin>
                </BoxContainer>
            </PageWrapper>
        );
    });
};

export default createUpdateWasteHandoverPlan;


